import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, UntypedFormControl, Validators } from '@angular/forms';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { CodeRepository } from '@data-layer/repositories';
import { CustomValidator, HTS_MASK, HTS_MASK_8 } from '@helpers/custom-validator';
import { of } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { HtsNumberModel } from '@data-layer/models/common';
import { DateHelper } from '@helpers/date.helper';
import { DateTimezoneService } from '@services/date-timezone.service';
import { UtilsHelper } from '@helpers/utils-helper';

@Component({
  selector: 'app-entry-hts-control',
  templateUrl: './entry-hts-control.component.html',
  styleUrls: ['./entry-hts-control.component.scss'],
})
export class EntryHtsControlComponent implements OnChanges {
  @Input() public fc: UntypedFormControl;
  @Input() public id: string = 'hts';
  @Input() public helper: string;
  @Input() public label: string;
  @Input() public placeholder: string = '';
  @Input() public shouldNotMatchHTSConfig: {
    relatedControlName: string | string[];
    errorMessage: string;
    relatedControlErrorMessage: string;
  };
  @Input() public shouldMatchUnits: string[];
  @Input() public matchLength: number[] = [8, 10];
  @Input() public arrivalDate: string;
  @Input() public isDisableValidation: boolean = false;
  @Input() public isRequired: boolean = false;
  @Input() public tooltipData: string;

  @Output() public getHtsUnit: EventEmitter<any> = new EventEmitter();

  public errors: IErrorForm = this.createErrors();
  public validationLoading: boolean = false;

  constructor(private codeRepository: CodeRepository, private dateTimezoneService: DateTimezoneService) {}

  public ngOnChanges(changes: SimpleChanges) {
    if (
      (changes.fc || changes.isDisableValidation || changes.matchLength || changes.isRequired || changes.shouldNotMatchHTSConfig) &&
      this.fc
    ) {
      this.updateFcValidators();
    }

    if (changes.matchLength) {
      this.errors = this.createErrors();
    }
  }

  public get maxLength(): number {
    return UtilsHelper.maxElement(this.matchLength);
  }

  public get mask(): string {
    switch (this.maxLength) {
      case 8:
        return HTS_MASK_8;

      case 10:
      default:
        return HTS_MASK;
    }
  }

  private updateFcValidators() {
    this.fc.clearValidators();
    this.fc.clearAsyncValidators();

    if (!this.isDisableValidation) {
      const validators = [CustomValidator.matchLength(this.matchLength)];
      if (this.isRequired) {
        validators.push(Validators.required);
      }
      if (this.shouldNotMatchHTSConfig) {
        validators.push(CustomValidator.notMatchRelatedHtsControl(this.shouldNotMatchHTSConfig));
      }
      this.fc.setValidators(validators);
    }
    this.fc.setAsyncValidators([this.validateHTSNumber.bind(this)]);
    if (this.fc.touched) {
      this.fc.updateValueAndValidity();
    }
  }

  private validateHTSNumber(control: AbstractControl) {
    const value = control.value;
    if (!value || !this.matchLength.includes(value.length)) {
      return of(null);
    }
    this.validationLoading = true;
    return this.codeRepository.searchHts(value).pipe(
      map((res: HtsNumberModel[]) => {
        if (res.length) {
          this.getHtsUnit.emit(res);
          if (this.isDisableValidation) {
            return null;
          }
          if (!this.isHasActiveHts(res)) {
            return { notMatchHtsNumber: 'FORM_ERROR.NOT_MATCH_HTS_NUMBER' };
          }
          const htsInfo: HtsNumberModel = res[0];
          if (this.shouldMatchUnits && htsInfo && !this.isMatchedHtsUnits(htsInfo)) {
            return { shouldMatchUnits: 'FORM_ERROR.HTS_HAS_DIFFER_UNITS' };
          }
          const hasParticipatingAgencies = res.some((item) => !!item.participatingAgencies);
          if (hasParticipatingAgencies) {
            return { hasParticipatingAgencies: 'FORM_ERROR.HTS_REQUIRED_PERMITS' };
          }

          return null;
        } else if (!this.isDisableValidation) {
          return { notMatchHtsNumber: 'FORM_ERROR.NOT_MATCH_HTS_NUMBER' };
        }
        return null;
      }),
      finalize(() => {
        this.validationLoading = false;
      }),
    );
  }

  private createErrors(): IErrorForm {
    return {
      required: 'FORM_ERROR.REQUIRED',
      notMatchHtsNumber: 'FORM_ERROR.NOT_MATCH_HTS_NUMBER',
      hasParticipatingAgencies: 'FORM_ERROR.HTS_REQUIRED_PERMITS',
      shouldNotMatchHts: 'FORM_ERROR.SHOULD_NOT_MATCH_HTS_NUMBER',
      shouldNotMatchSpecialChapterHts: 'FORM_ERROR.SHOULD_NOT_MATCH_SPECIAL_CHAPTER_HTS_NUMBER',
      matchLength: this.matchLength.every((length) => length === 8)
        ? 'FORM_ERROR.ENTRY_SHOULD_MATCH_HTS_LENGTH_8'
        : this.matchLength.every((length) => length === 10)
        ? 'FORM_ERROR.ENTRY_SHOULD_MATCH_HTS_LENGTH_10'
        : 'FORM_ERROR.ENTRY_SHOULD_MATCH_HTS_LENGTH',
    };
  }

  private isHasActiveHts(res: HtsNumberModel[]): boolean {
    const arrivalDate: string = this.arrivalDate;
    const controlDate: string = this.dateTimezoneService.startOfTheDay(arrivalDate || DateHelper.now);
    return res.some((hts) => {
      if (hts.startDate && hts.endDate) {
        return DateHelper.isSameOrBefore(hts.startDate, controlDate) && DateHelper.isSameOrBefore(controlDate, hts.endDate);
      }
      return false;
    });
  }

  private isMatchedHtsUnits(htsInfo: HtsNumberModel) {
    const htsUnits = [htsInfo.unit1, htsInfo.unit2, htsInfo.unit3].filter((unit) => !!unit);
    return htsUnits.every((unit) => this.shouldMatchUnits.includes(unit));
  }
}
